
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, Input, Button, DatePicker } from "antd";
import {
  APPLICATION_STATUSES,
} from "../../constants/recruitment.constants";
import type { ApplicationFilters as Filters } from "../../../../core/data/redux/recruitment/types";
import { selectJobs } from "../../../../core/data/redux/recruitment/selectors";
import { fetchJobs } from "../../../../core/data/redux/recruitment/thunks";
import type { AppDispatch } from "../../../../core/data/redux/store";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;

interface ApplicationFiltersProps {
  filters: Filters;
  onFilterChange: (partial: Partial<Filters>) => void;
}

const statusOpts = APPLICATION_STATUSES.map((s: string) => ({
  value: s,
  label: s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
}));

const scoreOpts = [
  { value: 0, label: "0+ (Any)" },
  { value: 20, label: "20+" },
  { value: 40, label: "40+" },
  { value: 60, label: "60+ (Good)" },
  { value: 80, label: "80+ (Strong)" },
];

const ApplicationFilters: React.FC<ApplicationFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [localDateRange, setLocalDateRange] = useState<[Dayjs, Dayjs] | null>(
    filters.date_from && filters.date_to
      ? [dayjs(filters.date_from), dayjs(filters.date_to)]
      : null,
  );

  const dispatch = useDispatch<AppDispatch>();
  const jobs = useSelector(selectJobs);

  useEffect(() => {
    if (jobs.length === 0) {
      dispatch(fetchJobs({ page: 1, limit: 100 }));
    }
  }, [dispatch, jobs.length]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status) count++;
    if (filters.min_score !== null) count++;
    if (filters.search) count++;
    if (filters.job_id) count++;
    if (filters.date_from || filters.date_to) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleReset = () => {
    setLocalSearch("");
    setLocalDateRange(null);
    onFilterChange({
      search: "",
      status: null,
      source: null,
      priority: null,
      job_id: null,
      min_score: null,
      max_score: null,
      date_from: null,
      date_to: null,
      page: 1,
    });
  };

  const handleSearchClick = () => {
    onFilterChange({ search: localSearch, page: 1 });
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setLocalDateRange([dates[0], dates[1]]);
      onFilterChange({
        date_from: dates[0].format("YYYY-MM-DD"),
        date_to: dates[1].format("YYYY-MM-DD"),
        page: 1,
      });
    } else {
      setLocalDateRange(null);
      onFilterChange({ date_from: null, date_to: null, page: 1 });
    }
  };

  const filterOption = (input: string, option?: { label: string; value: any }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-users text-primary fs-32 me-2" />
              Applications Pipeline
            </h4>
            <p className="text-muted mb-0">Monitor and manage candidate applications efficiently</p>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        
        <div className="d-flex gap-2">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search name, email, job..."
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (value === "") onFilterChange({ search: "", page: 1 });
              }}
              onPressEnter={handleSearchClick}
              style={{ width: 220, minHeight: 38 }}
              allowClear
            />
            <Button type="primary" onClick={handleSearchClick} style={{ minHeight: 38 }}>
              <i className="ti ti-search me-1" />
            </Button>
          </div>
        </div>

        
        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
          
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 170, minHeight: 38 }}
              placeholder="Search Job"
              value={filters.job_id || undefined}
              allowClear
              onChange={(value) => onFilterChange({ job_id: value || null, page: 1 })}
              options={jobs.map((j: any) => ({ value: j.id, label: j.job_title }))}
            />
          </div>

          
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 150, minHeight: 38 }}
              placeholder="Status"
              value={filters.status || undefined}
              allowClear
              onChange={(value) => onFilterChange({ status: value || null, page: 1 })}
              options={statusOpts}
            />
          </div>

          
          <div className="me-2 mb-2">
            <Select
              style={{ width: 130, minHeight: 38 }}
              placeholder="Min Score"
              value={filters.min_score !== null ? filters.min_score : undefined}
              allowClear
              onChange={(value) => onFilterChange({ min_score: value ?? null, page: 1 })}
              options={scoreOpts}
            />
          </div>

          
          <div className="me-2">
            <RangePicker
              size="small"
              value={localDateRange}
              onChange={(dates) =>
                handleDateRangeChange(dates as [Dayjs | null, Dayjs | null] | null)
              }
              allowClear
              placeholder={["Applied from", "Applied to"]}
              style={{ borderRadius: 8 }}
              format="DD MMM YYYY"
            />
          </div>

          
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"
                } mb-2 position-relative py-2`}
              onClick={handleReset}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFilters;
